<template>
  <r-ai-page-container :current-step="8">
    <!-- PR-AGENT-UX: celebration page. Replaces the previous 2-second
         auto-redirect with a real "you did it" moment. Shows what was
         captured (running summary), explains what to do next, and gives
         the user time to read before redirecting. -->
    <v-row v-if="!isUploading && !errorState">
      <v-col cols="12" class="pb-4 text-center">
        <div class="celebration-icon pb-4">
          <v-icon size="96" color="#10B981">
            mdi-check-decagram
          </v-icon>
        </div>
        <h2 class="celebration-title pb-2">
          Your career story is captured.
        </h2>
        <div class="text-body-1 text--secondary pb-3">
          Take a breath — we did the heavy lifting.
        </div>
        <div
          class="text-body-2 text--secondary pb-6 mx-auto"
          style="max-width: 520px; line-height: 1.5;"
        >
          It's all yours to build on — add a win or sharpen a story whenever
          you like.
        </div>

        <!-- PR-AIDONE-CTA: prominent "Next: Career Profile" card surfaced
             HERE, right after the headline, instead of buried under stats
             + STAR detail + at the very bottom of the page. The Career
             Profile is the natural home base — it's where the user sees
             everything captured, logs wins, and accesses every career
             tool. Making it the obvious next step (large card with
             description, primary color) means users don't need to scroll
             past two info cards to find the "what now?" answer.

             Resume generation is demoted to a small secondary link below
             — still reachable in one click, just visually de-emphasized
             so the primary path is unambiguous. -->
        <v-card
          class="next-step-card mx-auto mb-4"
          elevation="3"
          color="primary"
          dark
          @click="goToProfile"
          style="cursor: pointer; max-width: 560px;"
        >
          <v-card-text class="text-left d-flex align-center">
            <v-avatar size="48" color="white" class="mr-4 flex-shrink-0">
              <v-icon color="primary" large>mdi-view-dashboard-outline</v-icon>
            </v-avatar>
            <div class="flex-grow-1" style="min-width: 0;">
              <div class="text-overline white--text" style="opacity: 0.85; letter-spacing: 1px;">
                NEXT
              </div>
              <div class="text-h6 font-weight-bold white--text mb-1">
                Go to your Career Profile
              </div>
              <div class="text-body-2 white--text" style="opacity: 0.92; line-height: 1.4;">
                See everything we captured, log wins as they happen, refine
                your story with Sage, and unlock all your career tools.
              </div>
            </div>
            <v-icon color="white" large class="ml-2 flex-shrink-0">mdi-chevron-right</v-icon>
          </v-card-text>
        </v-card>

        <!-- Secondary path — generate a resume immediately. Smaller, link-
             style so it doesn't compete with the Career Profile CTA. -->
        <div class="text-center mb-6">
          <a
            href="#"
            class="text-body-2 secondary-link"
            @click.prevent="goToReview"
          >
            Or jump straight to generating a resume
            <v-icon small class="ml-1">mdi-arrow-right</v-icon>
          </a>
        </div>

        <!-- Running summary of what was captured -->
        <v-card
          outlined
          class="summary-card mx-auto mb-6"
          v-if="summaryStats.anyContent"
        >
          <v-card-text class="text-left">
            <div
              class="text-caption font-weight-bold mb-3"
              style="color: #6B21A8; letter-spacing: 0.4px;"
            >
              <v-icon x-small color="#8B5CF6" class="mr-1"
                >mdi-clipboard-check-outline</v-icon
              >
              HERE'S WHAT WE GOT
            </div>
            <div class="d-flex flex-wrap" style="gap: 12px">
              <div v-if="summaryStats.work" class="stat-pill">
                <span class="stat-num">{{ summaryStats.work }}</span>
                <span class="stat-label">
                  {{ summaryStats.work === 1 ? "job" : "jobs" }}
                </span>
              </div>
              <div v-if="summaryStats.education" class="stat-pill">
                <span class="stat-num">{{ summaryStats.education }}</span>
                <span class="stat-label">
                  {{ summaryStats.education === 1 ? "degree" : "degrees" }}
                </span>
              </div>
              <div v-if="summaryStats.hardSkills" class="stat-pill">
                <span class="stat-num">{{ summaryStats.hardSkills }}</span>
                <span class="stat-label">technical skills</span>
              </div>
              <div v-if="summaryStats.softSkills" class="stat-pill">
                <span class="stat-num">{{ summaryStats.softSkills }}</span>
                <span class="stat-label">leadership skills</span>
              </div>
              <div v-if="summaryStats.licenses" class="stat-pill">
                <span class="stat-num">{{ summaryStats.licenses }}</span>
                <span class="stat-label">credentials</span>
              </div>
              <div v-if="summaryStats.languages" class="stat-pill">
                <span class="stat-num">{{ summaryStats.languages }}</span>
                <span class="stat-label">languages</span>
              </div>
            </div>
            <div
              v-if="summaryStats.totalAchievements"
              class="text-caption text--secondary mt-3"
            >
              <v-icon x-small color="#10B981" class="mr-1"
                >mdi-trophy-outline</v-icon
              >
              {{ summaryStats.totalAchievements }} accomplishment{{
                summaryStats.totalAchievements === 1 ? "" : "s"
              }}
              captured in your own words.
            </div>
          </v-card-text>
        </v-card>

        <!-- PR-COACH-V2: STAR completion stats + why-it-matters. Replaces
             the soft "polish later whenever you're ready" with a brutally-
             honest "X of Y have full STAR detail (Z%)" + a concrete reason
             to act. The headline number is intentionally stark when it
             starts at 0/N — that's what motivates the click to finish. -->
        <v-card
          outlined
          class="star-status-card mx-auto mb-6"
          v-if="summaryStats.totalAchievements > 0"
        >
          <v-card-text class="text-left">
            <div class="d-flex align-baseline justify-space-between mb-2">
              <div
                class="text-caption font-weight-bold"
                style="color: #6B21A8; letter-spacing: 0.4px;"
              >
                <v-icon x-small color="#8B5CF6" class="mr-1"
                  >mdi-star-four-points-outline</v-icon
                >
                STAR DETAIL
              </div>
              <div class="text-caption font-weight-medium star-pct">
                {{ summaryStats.starCompletePercent }}% complete
              </div>
            </div>
            <v-progress-linear
              :value="summaryStats.starCompletePercent"
              color="#8B5CF6"
              background-color="#F3E8FF"
              height="6"
              rounded
              class="mb-3"
            ></v-progress-linear>
            <div class="text-body-2 text--primary mb-2">
              <strong
                >{{ summaryStats.starCompleteCount }} of
                {{ summaryStats.totalAchievements }}</strong
              >
              of your accomplishments have full STAR detail.
            </div>
            <div class="text-caption text--secondary mb-3">
              <strong>Why STAR matters:</strong> recruiters and AI both read
              your resume for proof of impact, not just activity. A bullet point
              is a claim; a STAR is evidence. Profiles with full STAR detail get
              more recruiter attention and stronger match scores.
            </div>
            <div class="d-flex flex-wrap" style="gap: 8px;">
              <v-btn
                color="primary"
                small
                @click="goFinishWithSage"
                :disabled="summaryStats.starCompletePercent >= 100"
              >
                <v-icon left small>mdi-microphone</v-icon>
                Finish with Sage Coach
              </v-btn>
              <v-btn
                outlined
                small
                class="ai-outlined-btn"
                @click="goFinishStepByStep"
                :disabled="summaryStats.starCompletePercent >= 100"
              >
                <v-icon left small>mdi-format-list-checks</v-icon>
                Finish step-by-step
              </v-btn>
            </div>
          </v-card-text>
        </v-card>

        <div class="text-body-1 pt-2 mx-auto" style="max-width: 480px">
          You can keep refining your profile in the main editor anytime — your
          data saves as you go.
        </div>
      </v-col>
    </v-row>
    <!-- PR-AIDONE-CTA: the previous bottom-of-page button row ("Go to my
         Career Profile" + "View / Generate Resume") is intentionally
         removed. Both destinations are now reachable above the fold —
         Career Profile as the prominent primary card, and resume
         generation as the smaller "Or jump straight to..." link directly
         below it. Keeping the bottom row would create three competing
         CTAs and undermine the new hierarchy. -->
  </r-ai-page-container>
</template>

<script>
export default {
  name: "AIDone",
  data: () => ({
    isUploading: false,
    errorState: false
  }),
  async mounted() {
    // PR-AGENT-UX: removed the 2-second auto-redirect. The celebration
    // page is the user's reward for completing the upload.
    //
    // PR-FIX-STAR-DISPLAY: reload Work from Cosmos before the summary
    // renders. The Coach saves STARs through ApproveStar (a separate write
    // path); the persisted work store on this page can be stale — or from
    // a different tab — so the STAR-detail count would read "0 of N" even
    // though the STAR is safely saved. Re-reading makes the count truthful.
    try {
      await this.$store.dispatch("work/loadWork");
    } catch (e) {
      /* non-fatal — summary falls back to the existing store state */
    }

    // PR-COACH-POINTER: clear the resume-where-left-off pointer ONLY when
    // STAR completion is 100% (flow truly done). AIDone is also reachable
    // mid-flow via "Save and Exit Agent" buttons; in that case the pointer
    // must survive so the user can resume from the same accomplishment
    // next session. summaryStats reads from the loadWork-fresh store, so
    // by this point starCompletePercent reflects reality.
    if (this.summaryStats && this.summaryStats.starCompletePercent >= 100) {
      this.$store.commit("work/clearCoachPointer");
    }
  },
  computed: {
    // PR-AGENT-UX: running tally of what was captured. Reads directly
    // from the Vuex modules each AI step populates. Fields that the
    // parser didn't extract just don't render — no "0 jobs" displayed.
    summaryStats() {
      var safeLen = arr => (Array.isArray(arr) ? arr.length : 0);
      var workList =
        this.$store.state.work && this.$store.state.work.workDict
          ? Object.values(this.$store.state.work.workDict)
          : [];
      var totalAch = 0;
      var starCompleteAch = 0;
      // PR-COACH-V2: an accomplishment is STAR-complete when all four
      // BRAM fields (Background/Responsibility/ActionResult/Measurement —
      // our internal storage shape for S/T/A/R) are non-empty. Anything
      // less and it's still a bullet, not a STAR.
      workList.forEach(w => {
        var ach = (w && (w.achievementList || w.achievements)) || [];
        ach.forEach(a => {
          totalAch += 1;
          if (
            a &&
            (a.background || "").trim() &&
            (a.responsibility || "").trim() &&
            (a.actionResult || "").trim() &&
            (a.measurement || "").trim()
          ) {
            starCompleteAch += 1;
          }
        });
      });
      var stats = {
        work: workList.length,
        education: safeLen(
          this.$store.state.education &&
            this.$store.state.education.educationDict
            ? Object.values(this.$store.state.education.educationDict)
            : []
        ),
        hardSkills: safeLen(
          this.$store.state.hardSkill && this.$store.state.hardSkill.hardSkills
        ),
        softSkills: safeLen(
          this.$store.state.softSkill && this.$store.state.softSkill.softSkills
        ),
        licenses: safeLen(
          this.$store.state.licenseCert &&
            this.$store.state.licenseCert.licensesCerts
        ),
        languages: safeLen(
          this.$store.state.language && this.$store.state.language.languages
        ),
        totalAchievements: totalAch,
        starCompleteCount: starCompleteAch,
        starCompletePercent:
          totalAch === 0 ? 0 : Math.round((starCompleteAch * 100) / totalAch)
      };
      stats.anyContent =
        stats.work +
          stats.education +
          stats.hardSkills +
          stats.softSkills +
          stats.licenses +
          stats.languages >
        0;
      return stats;
    }
  },
  methods: {
    goToProfile() {
      this.$router.push({ path: "/CareerProfile" });
    },
    goToReview() {
      this.$router.push({ path: "/Review" });
    },
    goFinishWithSage() {
      this.$router.push({ path: "/AIWorkAchievementsCoach" });
    },
    goFinishStepByStep() {
      this.$router.push({
        path: "/AIWorkAchievements",
        query: { mode: "step" }
      });
    }
  }
};
</script>

<style scoped>
.celebration-icon {
  animation: bounce-in 600ms ease-out;
}
@keyframes bounce-in {
  0% {
    transform: scale(0.3);
    opacity: 0;
  }
  60% {
    transform: scale(1.1);
    opacity: 1;
  }
  100% {
    transform: scale(1);
  }
}
.celebration-title {
  font-weight: 600;
  letter-spacing: -0.01em;
}
.summary-card {
  max-width: 560px;
  background: linear-gradient(135deg, #faf7ff 0%, #ffffff 100%);
}
.star-status-card {
  max-width: 560px;
  background: linear-gradient(135deg, #fef3f9 0%, #ffffff 100%);
  border-left: 4px solid #8b5cf6 !important;
}
.star-pct {
  color: #6b21a8;
}
.stat-pill {
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 999px;
  padding: 6px 14px;
}
.stat-num {
  font-size: 1.25rem;
  font-weight: 700;
  color: #6b21a8;
}
.stat-label {
  font-size: 0.85rem;
  color: #4b5563;
}
.upload-prompt {
  border: 2px dashed #aaaaaa;
  background-color: #ffffff;
  border-radius: 10px;
  padding: 32px;
  text-align: center;
  transition: all 0.3s ease;
  cursor: pointer;
}
.upload-prompt.drag-over {
  border-color: #1976d2;
  background-color: #e3f2fd;
  transform: scale(1.02);
}

/* PR-AIDONE-CTA: subtle lift on the primary "go to Career Profile" card
   so it feels clickable. Avoids relying on text underline / button
   chrome and matches the warm, finished-screen vibe. */
.next-step-card {
  transition: transform 200ms ease, box-shadow 200ms ease;
}
.next-step-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(123, 31, 162, 0.25) !important;
}

/* PR-AIDONE-CTA: secondary "or generate a resume" link. Reachable in
   one click but visually de-emphasized so the primary path is
   unambiguous. */
.secondary-link {
  color: #6b21a8;
  text-decoration: none;
  font-weight: 500;
  border-bottom: 1px dashed rgba(107, 33, 168, 0.4);
  padding-bottom: 1px;
}
.secondary-link:hover {
  color: #4c1d95;
  border-bottom-color: rgba(76, 29, 149, 0.7);
}

/* PR-FIX-DARKMODE: the summary + STAR cards keep a light gradient
   background by design. In dark mode Vuetify flips text--primary /
   text--secondary to light colors, which then vanish on the white
   card. Pin them dark so the cards stay readable in either theme. */
.theme--dark .summary-card .text--primary,
.theme--dark .star-status-card .text--primary {
  color: rgba(0, 0, 0, 0.87) !important;
}
.theme--dark .summary-card .text--secondary,
.theme--dark .star-status-card .text--secondary {
  color: rgba(0, 0, 0, 0.6) !important;
}
</style>
